/**
 * @description Enumeration of theme color variants used throughout the application
 * @example
 * ```typescript
 * import { ThemeColorsVariants } from './theme-colors-variants';
 * 
 * // Usage in components
 * const buttonColor = ThemeColorsVariants.Primary;
 * const alertType = ThemeColorsVariants.Danger;
 * ```
 */
export enum ThemeColorsVariants {
    /**
     * @description Primary brand color - typically blue
     * @value 'primary'
     * @example
     * ```typescript
     * // Used for main actions and brand elements
     * <button class="btn btn-primary">Submit</button>
     * ```
     */
    Primary = 'primary',

    /**
     * @description Secondary color - typically gray
     * @value 'secondary'
     * @example
     * ```typescript
     * // Used for secondary actions
     * <button class="btn btn-secondary">Cancel</button>
     * ```
     */
    Secondary = 'secondary',

    /**
     * @description Success color - typically green
     * @value 'success'
     * @example
     * ```typescript
     * // Used for success states and confirmations
     * <div class="alert alert-success">Operation completed successfully!</div>
     * ```
     */
    Success = 'success',

    /**
     * @description Danger color - typically red
     * @value 'danger'
     * @example
     * ```typescript
     * // Used for errors and destructive actions
     * <div class="alert alert-danger">An error occurred!</div>
     * ```
     */
    Danger = 'danger',

    /**
     * @description Warning color - typically yellow/orange
     * @value 'warning'
     * @example
     * ```typescript
     * // Used for warnings and cautions
     * <div class="alert alert-warning">Please review your input.</div>
     * ```
     */
    Warning = 'warning',

    /**
     * @description Info color - typically light blue
     * @value 'info'
     * @example
     * ```typescript
     * // Used for informational messages
     * <div class="alert alert-info">Here's some helpful information.</div>
     * ```
     */
    Info = 'info',

    /**
     * @description Light color - typically light gray
     * @value 'light'
     * @example
     * ```typescript
     * // Used for light themed elements
     * <div class="bg-light text-dark">Light content</div>
     * ```
     */
    Light = 'light',

    /**
     * @description Dark color - typically dark gray/black
     * @value 'dark'
     * @example
     * ```typescript
     * // Used for dark themed elements
     * <div class="bg-dark text-white">Dark content</div>
     * ```
     */
    Dark = 'dark',

    /**
     * @description White color - pure white
     * @value 'white'
     * @example
     * ```typescript
     * // Used for white backgrounds and text
     * <div class="bg-white text-dark">White content</div>
     * ```
     */
    White = 'white',

    /**
     * @description Body color - typically light gray
     * @value 'body'
     * @example
     * ```typescript
     * // Used for body text and backgrounds
     * <div class="text-body">Body text content</div>
     * ```
     */
    Body = 'body',

    /**
     * @description Tertiary color - additional accent color
     * @value 'tertiary'
     * @example
     * ```typescript
     * // Used for tertiary actions or accents
     * <button class="btn btn-tertiary">More Options</button>
     * ```
     */
    Tertiary = 'tertiary',

    /**
     * @description Black color - pure black
     * @value 'black'
     * @example
     * ```typescript
     * // Used for black text and backgrounds
     * <div class="bg-black text-white">Black content</div>
     * ```
     */
    Black = 'black',
}

/**
 * @description Union type for theme color variants that accepts string literals
 * @example
 * ```typescript
 * // Can use string literals
 * @Input() variant: ThemeColorVariant = 'primary';
 * ``` 
 */
export type ThemeColorVariantType = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark' | 'white' | 'body' | 'tertiary' | 'black';

/**
 * @description Union type for theme color variants that accepts both enum values and string literals
 * @example
 * ```typescript
 * // Can use enum values
 * @Input() variant: ThemeColorVariant = ThemeColorsVariants.Primary;
 * 
 * // Can use string literals
 * @Input() variant: ThemeColorVariant = 'primary';
 * 
 * // Can use in templates
 * <button [variant]="'success'">Success Button</button>
 * <button [variant]="ThemeColorsVariants.Danger">Danger Button</button>
 * ```
 */
export type IThemeColorVariant = ThemeColorsVariants | ThemeColorVariantType;
